import {
  ArrowLeft,
  ArrowRight,
  AudioLines,
  BookMarked,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Clock,
  Columns2,
  Download,
  FileText,
  Gauge,
  Loader2,
  PartyPopper,
  Pause,
  PenLine,
  Play,
  RotateCw,
  Scan,
  SkipBack,
  SkipForward,
  Volume2,
  X,
} from "lucide-react";
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { Leaf, Spinner } from "../components/ui";
import { latexToSpeech } from "../editor/speech";
import type { CompileJob } from "../lib/api";
import { timeAgo, useSession } from "../lib/app";
import { clock, describe, LABELS, PHASES, Toggle } from "./CompileButton";
import { keys } from "./MenuBar";
import "./compile.css";
import "./menubar.css";
import "./readaloud.css";

/*
 * The little films What's new plays to show a feature off: a pointer working a small copy of the
 * editor. They're built from the app's own classes, so they look like the real thing.
 */

/** Demos are drawn at this size, and shrunk to fit a narrower card. */
const STAGE_WIDTH = 600;

export interface DemoProps {
  /** Show one telling frame instead of playing (for reduced motion). */
  still: boolean;
}

export type Demo = ((props: DemoProps) => ReactNode) & {
  /** How long it plays before What's new moves on to the next feature, in ms. */
  duration: number;
};

function Stage({ inner, children }: { inner: RefObject<HTMLDivElement | null>; children: ReactNode }) {
  const outer = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(1);
  useLayoutEffect(() => {
    const el = outer.current!;
    const measure = () => setFit(Math.min(1, el.clientWidth / STAGE_WIDTH));
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, []);
  return (
    <div ref={outer} className="wn-stage" style={{ "--fit": fit } as CSSProperties} aria-hidden="true" inert>
      <div ref={inner} className="wn-stage-inner">
        {children}
      </div>
    </div>
  );
}

/** Plays a demo's script once. Leaving clears its timers, which stops it wherever it's got to. */
function useScript(still: boolean, script: (wait: (ms: number) => Promise<void>) => Promise<void>) {
  useEffect(() => {
    if (still) return;
    const timers: number[] = [];
    void script((ms) => new Promise((done) => timers.push(window.setTimeout(done, ms))));
    return () => timers.forEach((t) => window.clearTimeout(t));
    // Once per mount: What's new remounts a demo to replay it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [still]);
}

/** Re-render every quarter second while on, for the compiles' running clocks. */
function useTicking(on: boolean) {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => setN((n) => n + 1), 250);
    return () => window.clearInterval(t);
  }, [on]);
}

interface PointerAt {
  x: number;
  y: number;
  /** Goes up by one per click, to replay the press. */
  clicks: number;
}

/**
 * Where the point `fx` and `fy` of the way across an element is on the stage. Offsets rather than
 * client rects, which the card's open and close animations scale.
 */
function pointIn(stage: HTMLElement, el: HTMLElement, fx = 0.5, fy = 0.5) {
  let x = el.offsetWidth * fx;
  let y = el.offsetHeight * fy;
  for (let n: HTMLElement | null = el; n && n !== stage; n = n.offsetParent as HTMLElement | null) {
    x += n.offsetLeft;
    y += n.offsetTop;
  }
  return { x, y };
}

/** The demo's mouse. `to` glides it to an element: its middle, or `fx` and `fy` of the way across. */
function usePointer(stage: RefObject<HTMLDivElement | null>, home: { x: number; y: number }) {
  const [at, setAt] = useState<PointerAt>({ ...home, clicks: 0 });
  return {
    at,
    home: () => setAt({ ...home, clicks: 0 }),
    to(el: HTMLElement | null, fx = 0.5, fy = 0.5) {
      if (!el || !stage.current) return;
      const p = pointIn(stage.current, el, fx, fy);
      setAt((a) => ({ ...a, ...p }));
    },
    click: () => setAt((a) => ({ ...a, clicks: a.clicks + 1 })),
  };
}

function Pointer({ x, y, clicks }: PointerAt) {
  return (
    <div className="wn-pointer" style={{ transform: `translate(${x}px, ${y}px)` }}>
      {clicks > 0 && <span key={`ring${clicks}`} className="wn-ring" />}
      <svg key={`arrow${clicks}`} className={clicks ? "pressed" : undefined} width="18" height="22" viewBox="0 0 18 22">
        <path d="M2 1.5v16.2l4.1-3.9 2.7 6.2 2.9-1.3-2.7-6.1h5.6z" />
      </svg>
    </div>
  );
}

type Range = readonly [number, number];

/** Splits a line of LaTeX into runs coloured the way the editor does: commands, their arguments, maths. */
function tokens(line: string): { from: number; to: number; cls?: string }[] {
  const out: { from: number; to: number; cls?: string }[] = [];
  const push = (from: number, to: number, cls?: string) => to > from && out.push({ from, to, cls });
  let plain = 0;
  let i = 0;
  while (i < line.length) {
    if (line[i] === "$") {
      const end = line.indexOf("$", i + 1);
      const to = end < 0 ? line.length : end + 1;
      push(plain, i);
      push(i, to, "tok-keyword");
      i = plain = to;
    } else if (line[i] === "\\" && /[a-zA-Z]/.test(line[i + 1] ?? "")) {
      let name = i + 1;
      while (/[a-zA-Z]/.test(line[name] ?? "")) name++;
      let args = name;
      while (line[args] === "{" || line[args] === "[") {
        const close = line.indexOf(line[args] === "{" ? "}" : "]", args);
        if (close < 0) break;
        args = close + 1;
      }
      push(plain, i);
      push(i, name, "tok-typeName");
      push(name, args, "tok-atom");
      i = plain = args;
    } else i++;
  }
  push(plain, line.length);
  return out;
}

/** Cuts [from, to) at the points inside it. */
function pieces(from: number, to: number, at: number[]): Range[] {
  const cuts = [from, ...at.filter((p) => p > from && p < to).sort((a, b) => a - b), to];
  return cuts.slice(1).map((p, i) => [cuts[i], p] as const);
}

/** Editor marks (read aloud's highlights, spell check's underlines): a class over some ranges. */
interface Marks {
  cls: string;
  ranges: Range[];
}

/** A line, coloured, with marks over parts of it, given as offsets into the line. The first marks wrap the rest. */
function Line({ text, marks }: { text: string; marks: Marks[] }) {
  const runs = tokens(text);
  const paint = (from: number, to: number): ReactNode =>
    runs
      .filter((t) => t.to > from && t.from < to)
      .map((t) => {
        const a = Math.max(t.from, from);
        const run = text.slice(a, Math.min(t.to, to));
        return t.cls ? (
          <span key={a} className={t.cls}>
            {run}
          </span>
        ) : (
          <Fragment key={a}>{run}</Fragment>
        );
      });
  // Marks wrap the colouring, as CodeMirror's do, so a word made of several runs gets one pill.
  const layer =
    ({ cls, ranges }: Marks, inner: (from: number, to: number) => ReactNode) =>
    (from: number, to: number): ReactNode => {
      const live = ranges.filter((r) => r[1] > r[0] && r[1] > from && r[0] < to);
      if (!live.length) return inner(from, to);
      return pieces(from, to, live.flat()).map(([a, b]) =>
        live.some((r) => a >= r[0] && b <= r[1]) ? (
          <span key={a} className={cls}>
            {inner(a, b)}
          </span>
        ) : (
          <Fragment key={a}>{inner(a, b)}</Fragment>
        ),
      );
    };
  return marks.reduceRight((inner, m) => layer(m, inner), paint)(0, text.length);
}

/**
 * A few lines of the editor, with the caret at the end of line `caret` (counting from 0). Marks'
 * ranges are offsets into the lines joined with newlines, the way the editor reports them.
 */
function Code({ first, lines, caret, marks = [] }: { first: number; lines: string[]; caret?: number; marks?: Marks[] }) {
  let start = 0;
  return (
    <div className="wn-code">
      {lines.map((line, i) => {
        const at = start;
        start += line.length + 1;
        const local = marks.map((m) => ({ cls: m.cls, ranges: m.ranges.map((r) => [Math.max(0, r[0] - at), Math.min(line.length, r[1] - at)] as const) }));
        return (
          <div key={i} className={`wn-line${i === caret ? " active" : ""}`}>
            <span className="wn-gutter">{first + i}</span>
            <span className="wn-text">
              <Line text={line} marks={local} />
              {/* Remounted per keystroke, so it stays solid while typing and blinks once it stops. */}
              {i === caret && <span key={line.length} className="wn-caret" />}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const MENUS = ["File", "Edit", "Insert", "View", "Format", "Help"];

/** The top of the editor: the leaf, the project's name and the menus, with View's open below it if given. */
function TopBar({ view, viewMenu }: { view?: RefObject<HTMLButtonElement | null>; viewMenu?: ReactNode }) {
  return (
    <div className="wn-topbar">
      <span className="wn-home">
        <Leaf size={18} />
      </span>
      <span className="wn-title">Thesis</span>
      <div className="menubar">
        {MENUS.map((m) =>
          m === "View" ? (
            <span key={m} className="wn-anchor">
              <button ref={view} type="button" className={`mb-top${viewMenu ? " open" : ""}`}>
                {m}
              </button>
              {viewMenu}
            </span>
          ) : (
            <button key={m} type="button" className="mb-top">
              {m}
            </button>
          ),
        )}
        <button type="button" className="confetti-btn">
          <PartyPopper size={15} />
        </button>
      </div>
    </div>
  );
}

const RESULTS = ["\\section{Results}", "It works on every test."];
const ADDED = "Even the hard ones.";
const FIGURE = ["", "\\begin{figure}[h]", "  \\centering", "  \\includegraphics{plot}", "\\end{figure}"];

/** A whole-document compile, as the server lists it. */
function job(id: string, at: number, phase: CompileJob["phase"]): CompileJob {
  return { id, draft: false, only: null, force: false, by: null, requestedAt: at, startedAt: at, snapshotAt: at, phase, stoppedBy: null, shared: true, behindOthers: false };
}

/**
 * Recompile, then keep writing: the dot says the PDF won't have it, and the list of compiles
 * offers to restart this one with it. Then the PDF catches up.
 */
export function CompileDemo({ still }: DemoProps) {
  const stage = useRef<HTMLDivElement>(null);
  const recompile = useRef<HTMLButtonElement>(null);
  const restart = useRef<HTMLButtonElement>(null);
  const [start] = useState(Date.now);
  // The still: a compile that's been running a while, with an edit made since it started.
  const [jobs, setJobs] = useState<CompileJob[]>(still ? [job("1", start - 12_000, "typesetting")] : []);
  const [open, setOpen] = useState(still);
  const [typed, setTyped] = useState(still ? ADDED.length : 0);
  const [editedAt, setEditedAt] = useState<number | null>(still ? start - 4_000 : null);
  // The PDF on screen: when it was compiled, the edits it has, and whether it has the new sentence.
  const [pdf, setPdf] = useState({ at: start - 130_000, snapshotAt: start - 130_000, fresh: false });
  const pointer = usePointer(stage, { x: 470, y: 236 });
  useTicking(!still);

  useLayoutEffect(() => {
    if (still) pointer.to(restart.current, 0.35);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScript(still, async (wait) => {
    setJobs([]);
    setOpen(false);
    setTyped(0);
    setEditedAt(null);
    pointer.home();
    await wait(450);
    pointer.to(recompile.current, 0.4);
    await wait(650);
    pointer.click();
    let at = Date.now();
    setJobs([{ ...job("1", at, "preparing"), snapshotAt: null }]);
    await wait(450);
    setJobs([job("1", at, "typesetting")]);
    await wait(300);
    for (let i = 1; i <= ADDED.length; i++) {
      setTyped(i);
      setEditedAt(Date.now());
      await wait(45);
    }
    await wait(300);
    pointer.click();
    setOpen(true);
    await wait(1100);
    pointer.to(restart.current, 0.35);
    await wait(600);
    // Like the real one, the list closes and the compile stops, then starts again.
    pointer.click();
    setOpen(false);
    setJobs(([j]) => [{ ...j, stoppedBy: "you" }]);
    await wait(400);
    at = Date.now();
    setJobs([{ ...job("2", at, "preparing"), snapshotAt: null }]);
    await wait(450);
    setJobs([job("2", at, "typesetting")]);
    await wait(900);
    setJobs([job("2", at, "finishing")]);
    await wait(500);
    setJobs([]);
    setPdf({ at: Date.now(), snapshotAt: at, fresh: true });
    pointer.to(stage.current, 0.93, 0.8);
  });

  // As the real button works it out (CompileButton).
  const now = Date.now();
  const busy = jobs.length > 0;
  const running = jobs.find((j) => j.startedAt !== null);
  const state: keyof typeof LABELS = !busy ? "idle" : running?.stoppedBy ? "stopping" : running ? "compiling" : "waiting";
  const last = jobs[jobs.length - 1];
  const changedSince = (snapshot: number | null) => !!snapshot && !!editedAt && editedAt > snapshot;
  const stale = last ? last.startedAt !== null && changedSince(last.snapshotAt) : changedSince(pdf.snapshotAt);

  return (
    <Stage inner={stage}>
      <div className="wn-win wn-split">
        <div className="wn-editor">
          <div className="wn-tabs">
            <span className="wn-tab">
              <FileText size={13} /> main.tex
            </span>
          </div>
          <Code first={1} lines={[...RESULTS, ADDED.slice(0, typed), ...FIGURE]} caret={RESULTS.length} />
        </div>
        <div className="wn-pdf-col">
          <div className="pdf-toolbar">
            <div className={`split-btn compile-btn${busy ? " compiling" : ""}`}>
              <button ref={recompile} type="button" className="btn btn-primary">
                <Play size={14} />
                <span className="compile-label">
                  {Object.entries(LABELS).map(([k, label]) => (
                    <span key={k} className={k === state ? "on" : undefined}>
                      {label}
                    </span>
                  ))}
                </span>
                {stale && <span className="stale-dot" />}
              </button>
              <button type="button" className="btn btn-primary">
                {jobs.length > 1 && <span className="job-count">{jobs.length}</span>}
                <ChevronDown size={14} />
              </button>
            </div>
            <div className={`compile-status${busy ? " busy" : ""}`}>Compiled {timeAgo(pdf.at)}</div>
            <div className="grow" />
            <span className="icon-btn">
              <Download size={15} />
            </span>
          </div>
          <div className="wn-pdf">
            <div key={pdf.at} className={`wn-page${pdf.fresh ? " fresh" : ""}`}>
              <div className="wn-page-h">1&ensp;Results</div>
              <p>
                It works on every test.{pdf.fresh && <mark> {ADDED}</mark>}
              </p>
              <svg className="wn-plot" viewBox="0 0 120 56">
                <path d="M8 3v48h109" />
                <path d="M11 46c14-2 22-26 40-27s26-12 64-14" />
              </svg>
              <i />
              <i />
              <i />
              <i />
              <i />
            </div>
          </div>
          {open && (
            <div className="popover compile-pop">
              <div className="cp-heading">{busy ? "Compiling" : "Compiles"}</div>
              {!busy && <div className="cp-empty">Nothing is compiling.</div>}
              {jobs.map((j, i) => {
                const started = j.startedAt !== null;
                return (
                  <div key={j.id} className={`cp-job${started ? " running" : ""}`}>
                    <span className="cp-job-icon">{started ? <Loader2 size={14} className="cp-spin" /> : <Clock size={14} />}</span>
                    <div className="cp-job-body">
                      <div className="cp-job-title">{describe(j)}</div>
                      <div className="cp-job-meta">
                        {j.stoppedBy ? "Stopping…" : i === 0 && !started ? "Starting" : PHASES[j.phase]}
                        {" · "}
                        <span className="cp-clock">{clock(now - (j.startedAt ?? j.requestedAt))}</span>
                        {j.by && ` · ${j.by}`}
                      </div>
                      {started && changedSince(j.snapshotAt) && !j.stoppedBy && (
                        <div className="cp-job-behind">
                          Edited since it started, so it won't have those changes.
                          <button ref={restart} type="button" className="cp-link">
                            Restart with them
                          </button>
                        </div>
                      )}
                    </div>
                    {j.phase !== "finishing" && (
                      <button type="button" className="btn btn-sm cp-stop" disabled={!!j.stoppedBy}>
                        {started ? "Stop" : "Cancel"}
                      </button>
                    )}
                  </div>
                );
              })}
              {busy && (
                <button type="button" className="menu-item">
                  <span className="menu-icon">
                    <Play size={14} />
                  </span>
                  Recompile after {jobs.length > 1 ? "these" : "this"}
                </button>
              )}
              {changedSince(pdf.snapshotAt) ? (
                <div className="cp-pdf">
                  <CircleDot size={13} className="cp-warn" />
                  <span>The PDF on screen is from {timeAgo(pdf.at)}. The project has changed since.</span>
                </div>
              ) : (
                <div className="cp-pdf">
                  <Check size={13} className="cp-ok" />
                  <span>The PDF on screen is up to date.</span>
                </div>
              )}
              <div className="menu-sep" />
              <Toggle label="Auto-compile" on={false} onToggle={() => {}} />
              <Toggle label="Draft mode: images as boxes" hint="fast" on={false} onToggle={() => {}} />
              <Toggle label="Only the chapter I'm editing" hint="\includeonly" on={false} onToggle={() => {}} />
            </div>
          )}
        </div>
      </div>
      <Pointer {...pointer.at} />
    </Stage>
  );
}
CompileDemo.duration = 8_800;

const PASSAGE = ["\\section{Results}", "The ratio $\\tau = I_{out}/I_{in}$ fell to 0.4,", "as \\textcite{peng2017} predicted.", "See Figure~\\ref{fig:wicket-door}.", "", "\\begin{figure}[h]"];
/** The demo starts partway through a file this long, in sentences. */
const FILE_SENTENCES = 82;
const WORD_MS = 240;

/** What read aloud says for the passage, word by word, with the part of the passage each word comes from. */
function speakPassage() {
  try {
    const citations = new Map([["peng2017", { author: "Peng, X. and Wang, Y. and Li, Z.", year: "2017" }]]);
    return latexToSpeech(PASSAGE.join("\n"), { citations }).map((s) => ({
      range: [s.from, s.to] as const,
      words: [...s.text.matchAll(/\S+/g)].map((m) => ({ said: m[0], range: [s.starts[m.index!], s.ends[m.index! + m[0].length - 1]] as const })),
    }));
  } catch {
    // The demo shows the player without the words, rather than take the card down with it.
    return [];
  }
}

/** The top of the View menu, down to Read aloud. */
function ViewMenu({ item, hover }: { item: RefObject<HTMLButtonElement | null>; hover: boolean }) {
  const row = (icon: ReactNode, label: string, opts: { hint?: string; checked?: boolean; ref?: RefObject<HTMLButtonElement | null>; active?: boolean; more?: boolean } = {}) => (
    <div className="mb-row">
      <button ref={opts.ref} type="button" className={`mb-item${opts.active ? " active" : ""}${opts.checked ? " checked" : ""}`}>
        <span className="mb-lead">{opts.checked ? <Check size={15} /> : icon}</span>
        <span className="mb-label">{label}</span>
        {opts.hint && <kbd>{opts.hint}</kbd>}
        {opts.more && <ChevronRight size={14} className="mb-chevron" />}
      </button>
    </div>
  );
  return (
    <div className="mb-menu">
      <div className="mb-heading">Layout options</div>
      {row(<Columns2 size={15} />, "Split view", { checked: true })}
      {row(<PenLine size={15} />, "Editor only")}
      {row(<FileText size={15} />, "PDF only")}
      <div className="mb-sep" />
      {row(<Scan size={15} />, "Focus mode", { hint: keys("Mod-Shift-m") })}
      {row(<AudioLines size={15} />, "Read aloud", { hint: keys("Mod-Shift-u"), ref: item, active: hover })}
      <div className="mb-sep" />
      {row(null, "Editing mode", { more: true })}
    </div>
  );
}

/** ReadAloudBar's markup: the real one needs a live editor panel. */
function ReadBar({ status, sentence }: { status: "loading" | "playing" | "done"; sentence: number }) {
  const playing = status !== "done";
  const detail = status === "playing" ? `Sentence ${sentence} of ${FILE_SENTENCES} · about 6 min left` : status === "done" ? "Play reads from the cursor again" : "";
  return (
    <div className={`ra-bar ra-${status}`}>
      {playing && <div className="ra-progress" style={{ "--p": sentence / FILE_SENTENCES } as CSSProperties} />}
      <div className="ra-controls">
        <button type="button" className="icon-btn ra-skip">
          <SkipBack size={15} />
        </button>
        <button type="button" className="ra-play">
          {playing ? <Pause size={15} fill="currentColor" strokeWidth={0} /> : <Play size={15} fill="currentColor" strokeWidth={0} className="ra-play-icon" />}
        </button>
        <button type="button" className="icon-btn ra-skip">
          <SkipForward size={15} />
        </button>
      </div>
      <div className="ra-status">
        <span className="ra-wave">
          <i />
          <i />
          <i />
          <i />
        </span>
        <span className="ra-label">{status === "loading" ? "Getting ready…" : playing ? "Reading" : "Finished"}</span>
        {detail && <span className="ra-detail">{detail}</span>}
      </div>
      <label className="ra-select ra-rate">
        <Gauge size={14} />
        <select>
          <option>1.25×</option>
        </select>
      </label>
      <label className="ra-select ra-voice">
        <select>
          <option>Karen · Australian</option>
        </select>
      </label>
      <button type="button" className="icon-btn ra-close">
        <X size={16} />
      </button>
    </div>
  );
}

/**
 * Read aloud from the View menu: the player docks along the bottom of the editor, and each word
 * lights up as it's said, maths and citations included. Subtitles stand in for the voice.
 */
export function ReadAloudDemo({ still }: DemoProps) {
  const stage = useRef<HTMLDivElement>(null);
  const view = useRef<HTMLButtonElement>(null);
  const item = useRef<HTMLButtonElement>(null);
  const [spoken] = useState(speakPassage);
  const [menu, setMenu] = useState<"open" | "hover" | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "playing" | "done">(still ? "playing" : "idle");
  // The sentence and word being said. The still has "tau" lit, from the maths.
  const [at, setAt] = useState<[number, number]>(still ? [1, 2] : [-1, -1]);
  const pointer = usePointer(stage, { x: 470, y: 236 });

  useLayoutEffect(() => {
    if (still) pointer.to(stage.current, 0.9, 0.42);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScript(still, async (wait) => {
    setMenu(null);
    setStatus("idle");
    setAt([-1, -1]);
    pointer.home();
    await wait(450);
    pointer.to(view.current);
    await wait(600);
    pointer.click();
    setMenu("open");
    await wait(450);
    pointer.to(item.current, 0.3);
    await wait(350);
    setMenu("hover");
    await wait(250);
    pointer.click();
    setMenu(null);
    setStatus("loading");
    await wait(150);
    pointer.to(stage.current, 0.9, 0.42);
    await wait(350);
    setStatus("playing");
    for (let s = 0; s < spoken.length; s++) {
      for (let w = 0; w < spoken[s].words.length; w++) {
        setAt([s, w]);
        await wait(WORD_MS);
      }
      await wait(200);
    }
    setStatus("done");
    setAt([-1, -1]);
  });

  const sentence = spoken[at[0]];
  const word = sentence?.words[at[1]];

  return (
    <Stage inner={stage}>
      <div className="wn-win wn-whole">
        <TopBar view={view} viewMenu={menu && <ViewMenu item={item} hover={menu === "hover"} />} />
        <div className="wn-editor-area">
          <Code
            first={41}
            lines={PASSAGE}
            caret={status === "idle" ? 0 : undefined}
            marks={[
              { cls: "cm-ra-sentence", ranges: sentence ? [sentence.range] : [] },
              { cls: "cm-ra-word", ranges: word ? [word.range] : [] },
            ]}
          />
          {status === "playing" && sentence && (
            <div className="wn-caption">
              <Volume2 size={13} />
              <span>
                {sentence.words.map((w, i) => (
                  <Fragment key={i}>
                    {i > 0 && " "}
                    <span className={i === at[1] ? "said" : undefined}>{w.said}</span>
                  </Fragment>
                ))}
              </span>
            </div>
          )}
        </div>
        {status !== "idle" && (
          <div className="cm-read-aloud wn-dock">
            <ReadBar status={status} sentence={40 + Math.max(1, at[0] + 1)} />
          </div>
        )}
      </div>
      <Pointer {...pointer.at} />
    </Stage>
  );
}
ReadAloudDemo.duration = 10_200;

const spellLines = (colour: boolean, most: boolean) => [
  `The ${most ? "most" : "msot"} surprising result: the ${colour ? "colour" : "color"} of`,
  "the samples barely changed once the tradies",
  "fitted the batts in the arvo, as",
  "\\textcite{biot1956} suggested.",
  "",
  "\\begin{tabular}{lcccc}",
];
/** What the old spell check underlined. The new one only flags the typo, and shows color as American. */
const FLAGGED_BEFORE = ["msot", "color", "tradies", "batts", "arvo", "lcccc"];

type MenuEntry = readonly [label: string, hint?: string] | "-";
const COLOR_MENU: MenuEntry[] = [["colour", "Australian"], ["colon"], ["Colo"], "-", ["Add “color” to dictionary"]];
const MSOT_MENU: MenuEntry[] = [["most"], ["moot"], ["mot"], ["MST"], ["sot"], "-", ["Add “msot” to dictionary"]];

/**
 * The new spell check goes over a page the old one had covered in false alarms. They go, color turns
 * blue as an American spelling, and right-clicks put in the Australian spelling and fix a typo.
 */
export function SpellDemo({ still }: DemoProps) {
  const stage = useRef<HTMLDivElement>(null);
  // How many lines the new spell check has been over.
  const [checked, setChecked] = useState(still ? Infinity : 0);
  const [colour, setColour] = useState(false);
  const [most, setMost] = useState(false);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const [menu, setMenu] = useState<{ items: MenuEntry[]; x: number; y: number; hover: number } | null>(null);
  // The word just put in, which flashes.
  const [fixed, setFixed] = useState<string | null>(null);
  const pointer = usePointer(stage, { x: 470, y: 236 });

  /** A right-click on the word with this underline: the menu opens where the pointer is. */
  const word = (cls: string) => stage.current?.querySelector<HTMLElement>(`.wn-win .${cls}`) ?? null;
  const rightClick = (el: HTMLElement | null, items: MenuEntry[], hover = -1) => {
    if (el && stage.current) setMenu({ items, ...pointIn(stage.current, el), hover });
  };

  useLayoutEffect(() => {
    if (!still) return;
    const variant = word("cm-spelling-variant");
    pointer.to(variant);
    rightClick(variant, COLOR_MENU, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScript(still, async (wait) => {
    setChecked(0);
    setColour(false);
    setMost(false);
    setTip(null);
    setMenu(null);
    setFixed(null);
    pointer.home();
    await wait(500);
    for (let line = 1; line <= spellLines(false, false).length; line++) {
      setChecked(line);
      await wait(120);
    }
    await wait(300);
    const variant = word("cm-spelling-variant");
    pointer.to(variant);
    await wait(750);
    // Hovering says why it's blue, as the editor's tooltip does.
    if (variant && stage.current) {
      const { x, y } = pointIn(stage.current, variant, 0.5, 1);
      setTip({ x: x + 6, y: y + 12 });
    }
    await wait(900);
    setTip(null);
    pointer.click();
    rightClick(variant, COLOR_MENU);
    await wait(450);
    const first = () => stage.current?.querySelector<HTMLElement>(".ctx-menu button") ?? null;
    pointer.to(first(), 0.3);
    await wait(650);
    setMenu((m) => m && { ...m, hover: 0 });
    await wait(250);
    pointer.click();
    setMenu(null);
    setColour(true);
    setFixed("colour");
    await wait(600);
    const typo = word("cm-misspelt");
    pointer.to(typo);
    await wait(750);
    pointer.click();
    rightClick(typo, MSOT_MENU);
    await wait(450);
    pointer.to(first(), 0.3);
    await wait(650);
    setMenu((m) => m && { ...m, hover: 0 });
    await wait(250);
    pointer.click();
    setMenu(null);
    setMost(true);
    setFixed("most");
    await wait(500);
    pointer.to(stage.current, 0.9, 0.78);
  });

  const lines = spellLines(colour, most);
  const doc = lines.join("\n");
  const find = (w: string): Range | null => {
    const i = doc.indexOf(w);
    return i < 0 ? null : [i, i + w.length];
  };
  const misspelt: Range[] = [];
  const variants: Range[] = [];
  for (const w of FLAGGED_BEFORE) {
    const r = find(w);
    if (!r) continue;
    // Lines the new check hasn't reached yet still show the old underlines.
    const line = doc.slice(0, r[0]).split("\n").length - 1;
    if (line >= checked || w === "msot") misspelt.push(r);
    else if (w === "color") variants.push(r);
  }
  const flash = fixed ? find(fixed) : null;

  return (
    <Stage inner={stage}>
      <div className="wn-win">
        <TopBar />
        <Code
          first={41}
          lines={lines}
          marks={[
            { cls: "cm-misspelt", ranges: misspelt },
            { cls: "cm-spelling-variant", ranges: variants },
            { cls: "wn-fixed", ranges: flash ? [flash] : [] },
          ]}
        />
        {/* The new check sweeping down the page. */}
        {checked > 0 && Number.isFinite(checked) && <div className={`wn-sweep${checked >= lines.length ? " done" : ""}`} style={{ "--line": checked } as CSSProperties} />}
      </div>
      {tip && (
        <div className="wn-tip" style={{ left: tip.x, top: tip.y }}>
          American spelling. Australian: colour
        </div>
      )}
      {menu && (
        <div className="ctx-menu" style={{ left: menu.x + 2, top: menu.y + 2 }}>
          {menu.items.map((item, i) =>
            item === "-" ? (
              <div key={i} className="ctx-sep" />
            ) : (
              <button key={i} type="button" className={i === menu.hover ? "wn-hover" : undefined}>
                <span>{item[0]}</span>
                {item[1] && <kbd>{item[1]}</kbd>}
              </button>
            ),
          )}
        </div>
      )}
      <Pointer {...pointer.at} />
    </Stage>
  );
}
SpellDemo.duration = 9_000;

const DISCUSSION = [
  "\\section{Discussion}",
  "",
  "The perforated boards were quieter",
  "than we expected at low frequencies,",
  "which the model in Section~\\ref{sec:model}",
  "does not predict.",
  "",
];
const BIB = [
  "@article{biot1956,",
  "  author = {Biot, M. A.},",
  "  title = {Theory of propagation of elastic",
  "           waves in a porous solid},",
  "  year = {1956},",
  "}",
  "",
];
/** Selected in main.tex: from "quieter" to the end of the next line, where the cursor is. */
const KEPT: Range = (() => {
  const doc = DISCUSSION.join("\n");
  return [doc.indexOf("quieter"), doc.indexOf("frequencies,") + "frequencies,".length];
})();
const FILES = [
  { path: "main.tex", icon: <FileText size={14} /> },
  { path: "refs.bib", icon: <BookMarked size={14} /> },
];

/**
 * Deep in a file with some text selected, a refresh brings it all back: the same lines, the same
 * selection. So does going to another file and back.
 */
export function PlaceDemo({ still }: DemoProps) {
  const stage = useRef<HTMLDivElement>(null);
  const reload = useRef<HTMLButtonElement>(null);
  const [file, setFile] = useState("main.tex");
  const [loading, setLoading] = useState(false);
  // Goes up each time main.tex comes back, to flash where it came back to.
  const [back, setBack] = useState(0);
  const pointer = usePointer(stage, { x: 470, y: 236 });
  const tab = (path: string) => stage.current?.querySelector<HTMLElement>(`.etab[data-path="${path}"]`) ?? null;

  useLayoutEffect(() => {
    if (still) pointer.to(reload.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScript(still, async (wait) => {
    setFile("main.tex");
    setLoading(false);
    setBack(0);
    pointer.home();
    await wait(500);
    pointer.to(reload.current);
    await wait(650);
    pointer.click();
    setLoading(true);
    await wait(900);
    setLoading(false);
    setBack((n) => n + 1);
    await wait(1100);
    pointer.to(tab("refs.bib"));
    await wait(650);
    pointer.click();
    setFile("refs.bib");
    await wait(900);
    pointer.to(tab("main.tex"));
    await wait(650);
    pointer.click();
    setFile("main.tex");
    setBack((n) => n + 1);
    await wait(400);
    pointer.to(stage.current, 0.9, 0.8);
  });

  const tex = file === "main.tex";
  return (
    <Stage inner={stage}>
      <div className="wn-win">
        <div className="wn-browser">
          <span className="wn-dots">
            <i />
            <i />
            <i />
          </span>
          <ArrowLeft size={14} />
          <ArrowRight size={14} />
          <button ref={reload} type="button" className="wn-reload">
            <RotateCw size={13} />
          </button>
          <span className="wn-url">benleaf.bencockburn.com.au/project/thesis</span>
          {loading && <span className="wn-loadbar" />}
        </div>
        {loading ? (
          <div className="wn-loading">
            <Spinner size={18} />
          </div>
        ) : (
          <>
            <TopBar />
            <div className="etabs">
              <div className="etabs-strip">
                {FILES.map((f) => (
                  <div key={f.path} data-path={f.path} className={`etab${f.path === file ? " active" : ""}`}>
                    <span className="etab-icon">{f.icon}</span>
                    <span className="etab-name">{f.path}</span>
                    <button type="button" className="etab-close">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            {/* Remounted on the way back, so the flash plays each time. */}
            <Code
              key={`${file}.${back}`}
              first={tex ? 212 : 1}
              lines={tex ? DISCUSSION : BIB}
              caret={tex ? 3 : 0}
              marks={tex ? [{ cls: "wn-sel", ranges: [KEPT] }, { cls: "wn-fixed", ranges: back ? [KEPT] : [] }] : []}
            />
          </>
        )}
      </div>
      <Pointer {...pointer.at} />
    </Stage>
  );
}
PlaceDemo.duration = 7_200;

const CONFETTI_COLOURS = ["#f2c446", "#e8604c", "#3f9a6b", "#4a7fd6", "#b06fd8", "#f08fb0", "#2fb3b0"];

/** Confetti for one of the demo's screens: the same colours and shapes as the real thing, falling by CSS. */
function rain(seed: number) {
  let n = seed;
  const random = () => (n = (n * 16807) % 2147483647) / 2147483647;
  return Array.from({ length: 34 }, () => {
    const shape = random();
    const size = 5 + random() * 5;
    return {
      "--x": `${random() * 100}%`,
      "--y": `${8 + random() * 80}%`,
      "--w": `${shape < 0.2 ? size * 0.3 : size}px`,
      "--h": `${shape < 0.2 ? size * 2 : shape < 0.4 ? size : size * 0.6}px`,
      "--r": shape >= 0.2 && shape < 0.4 ? "50%" : "1px",
      "--c": CONFETTI_COLOURS[Math.floor(random() * CONFETTI_COLOURS.length)],
      "--a": `${random() * 360}deg`,
      "--sway": `${(random() - 0.5) * 50}px`,
      "--d": `${1.5 + random() * 1.1}s`,
      "--delay": `${random() * 1.2}s`,
    } as CSSProperties;
  });
}
const RAIN = [rain(7), rain(31)];

/** One person's screen in the confetti demo: a project, its party popper, and whose screen it is. */
function Screen({ side, title, who, popper, lines, first, children }: {
  side: "left" | "right";
  title: string;
  who: string;
  popper: ReactNode;
  lines: string[];
  first: number;
  children?: ReactNode;
}) {
  return (
    <div className={`wn-win wn-${side}`}>
      <div className="wn-topbar">
        <span className="wn-home">
          <Leaf size={18} />
        </span>
        <span className="wn-title">{title}</span>
        {popper}
        <span className="grow" />
        <span className="wn-who">{who}</span>
      </div>
      <Code first={first} lines={lines} />
      {children}
    </div>
  );
}

/**
 * The party popper, pressed on your screen, rains confetti on everyone with a project open: here,
 * Alex on another project too, with your name at the top. A second press within the hour won't go.
 */
export function ConfettiDemo({ still }: DemoProps) {
  const stage = useRef<HTMLDivElement>(null);
  const popper = useRef<HTMLButtonElement>(null);
  const { user } = useSession();
  const [falling, setFalling] = useState<"in" | "leaving" | null>(still ? "in" : null);
  const [sent, setSent] = useState(still);
  // Bumped per press, to replay the pop or the shake.
  const [presses, setPresses] = useState(0);
  const [toast, setToast] = useState(false);
  const pointer = usePointer(stage, { x: 300, y: 236 });

  useLayoutEffect(() => {
    if (still) pointer.to(popper.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScript(still, async (wait) => {
    setFalling(null);
    setSent(false);
    setPresses(0);
    setToast(false);
    pointer.home();
    await wait(500);
    pointer.to(popper.current);
    await wait(700);
    pointer.click();
    setPresses(1);
    setFalling("in");
    await wait(300);
    setSent(true);
    await wait(2900);
    setFalling("leaving");
    await wait(600);
    // Once an hour: the next press just shakes its head.
    pointer.click();
    setPresses(2);
    setToast(true);
  });

  const confetti = (i: number) =>
    falling && (
      <div className={`wn-rain${still ? " still" : ""}`}>
        {RAIN[i].map((style, j) => (
          <i key={j} style={style} />
        ))}
      </div>
    );
  const banner = (who: string) =>
    falling && (
      <div className={`confetti-banner${falling === "leaving" ? " leaving" : ""}`}>
        🎉 <strong>{who}</strong> sent everyone confetti
      </div>
    );

  return (
    <Stage inner={stage}>
      <Screen
        side="left"
        title="Thesis"
        who="You"
        first={205}
        lines={["\\chapter{Conclusion}", "", "It's finished. Time to", "celebrate with everyone."]}
        popper={
          <button ref={popper} key={presses} type="button" className={`confetti-btn${sent ? " waiting" : ""}${presses === 2 ? " nope" : presses === 1 ? " popped" : ""}`}>
            <PartyPopper size={15} />
          </button>
        }
      >
        {confetti(0)}
        {banner("You")}
        {toast && (
          <div className="wn-toasts">
            <div className="toast">You've had your confetti for this hour. You can send more in 59 minutes.</div>
          </div>
        )}
      </Screen>
      <Screen
        side="right"
        title="Lab report"
        who="Alex"
        first={12}
        lines={["\\section{Method}", "", "We clamped each sample", "and swept 50 Hz to 5 kHz."]}
        popper={
          <button type="button" className="confetti-btn">
            <PartyPopper size={15} />
          </button>
        }
      >
        {confetti(1)}
        {banner(user?.displayName ?? "Someone")}
      </Screen>
      <Pointer {...pointer.at} />
    </Stage>
  );
}
ConfettiDemo.duration = 7_400;
